import { InputState } from "@studio3t/generic-components";
import { useQueryClient } from "@tanstack/react-query";
import { scriptsApi, servicesApi } from "@/api/client";
import { queryKeys } from "@/hooks/queryKeys";
import { useScripts, useServices } from "@/hooks/useApi";
import {
  parseResourceId,
  type StagedResource,
  usePipelineStaging,
} from "./pipelineStaging";

// Script filenames carry the extension; a typed name gets it for free.
const withRhaiExtension = (name: ScriptName): Filename =>
  name.includes(".") ? name : `${name}.rhai`;

// Lightweight domain aliases for the primitive arguments below (the
// pipelineLint pattern) — they name what a string means at a call site
// without runtime cost.
type ServiceName = string;
type ScriptName = string;
type Filename = string;
type RowId = string;

/** Where a brand-new script goes: the untitled row it comes from, its
 *  home service and the typed name (.rhai appended on save). */
interface ScriptHome {
  untitledId: RowId;
  serviceName: ServiceName;
  name: ScriptName;
}

/** A persisted script the staged row should adopt. */
interface SavedScript {
  untitledId: RowId;
  serviceName: ServiceName;
  filename: Filename;
  /** The text as saved — the row's stored content from here on. */
  content: string;
}

// Renames keep the folder: only the last path segment changes.
const renamedFilename = (resourceName: Filename, name: ScriptName): Filename =>
  resourceName.replace(/[^/]*$/, withRhaiExtension(name));

// The rename's file mechanics: copy the stored content under the new
// name, then delete the old file. A delete refusal (409 when a deployed
// pipeline references the file — the backend owns that rule) backs out
// the copy so the rename fails cleanly.
async function moveScriptFile(
  serviceName: ServiceName,
  oldFilename: Filename,
  newFilename: Filename,
): Promise<void> {
  const stored = await scriptsApi.get(serviceName, oldFilename);
  if (typeof stored?.content !== "string") throw new Error("Script not found");
  await scriptsApi.create(serviceName, newFilename, stored.content);
  try {
    await scriptsApi.delete(serviceName, oldFilename);
  } catch (err) {
    await scriptsApi.delete(serviceName, newFilename).catch(() => {});
    throw err;
  }
}

// Everything script editing needs, mirroring useSchemaSaving: the UDF
// service, existing filenames for the duplicate check, the row save
// (persist edits under the same filename), the rename (a new filename IS
// a new resource id: create + delete, then re-point the staged rows) and
// the untitled-draft save (persist a brand-new script and stage it).
export function useScriptSaving() {
  const {
    scriptDrafts,
    clearScriptDraft,
    rememberSavedScript,
    markScriptSaved,
    renameTransform,
    effectiveSelectedId,
  } = usePipelineStaging();
  const queryClient = useQueryClient();
  const { data: services } = useServices();
  const scriptServiceName =
    services?.find((s) => s.provider === "udf")?.name ?? null;
  const { data: existingScripts } = useScripts(scriptServiceName ?? "");

  const refreshScriptLists = () => {
    if (!scriptServiceName) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.scripts(scriptServiceName),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.serviceResources(scriptServiceName),
    });
  };

  // Persists a transform row's edits from its draft under its existing
  // filename.
  const saveRow = async (item: StagedResource) => {
    const draft = scriptDrafts[item.id];
    const target = parseResourceId(item.id);
    if (draft == null || !target) return;
    await scriptsApi.update(target.serviceName, target.resourceName, draft);
    if (effectiveSelectedId === item.id) markScriptSaved();
    rememberSavedScript(item.id, draft);
    clearScriptDraft(item.id);
    refreshScriptLists();
  };

  // Renames a script file: the filename is the resource id, so this is a
  // create-under-the-new-name + delete-the-old, then the staged rows,
  // drafts and selection re-point (renameTransform). The STORED content
  // moves — unsaved edits stay in the row's draft across the rename.
  const renameRow = async (
    item: StagedResource,
    name: string,
  ): Promise<boolean> => {
    const target = parseResourceId(item.id);
    if (!target) return false;
    const newFilename = renamedFilename(target.resourceName, name);
    if (newFilename === target.resourceName) return true;
    try {
      await moveScriptFile(
        target.serviceName,
        target.resourceName,
        newFilename,
      );
    } catch {
      return false;
    }
    renameTransform(item.id, {
      id: `resource:${target.serviceName}:${newFilename}`,
      name: newFilename,
    });
    refreshScriptLists();
    return true;
  };

  // The "Save as" fork of an in-use script's edits: the row's draft
  // becomes a NEW file (in the script's own service, keeping the folder)
  // and the staged row swaps to it immediately — the original file and
  // the pipelines running it stay untouched.
  const saveRowAs = async (item: StagedResource, name: ScriptName) => {
    const target = parseResourceId(item.id);
    if (!target) return;
    const draft = scriptDrafts[item.id];
    const content =
      draft ??
      (await scriptsApi.get(target.serviceName, target.resourceName))?.content;
    if (typeof content !== "string") return;
    const newFilename = renamedFilename(target.resourceName, name);
    await scriptsApi.create(target.serviceName, newFilename, content);
    const newId = `resource:${target.serviceName}:${newFilename}`;
    if (effectiveSelectedId === item.id) markScriptSaved();
    renameTransform(item.id, { id: newId, name: newFilename });
    // The fork owns the edits now — the draft renameTransform carried
    // over to the new id is already persisted.
    rememberSavedScript(newId, content);
    clearScriptDraft(newId);
    refreshScriptLists();
  };

  // The shared tail of saving an untitled row's draft as a brand-new
  // script: the staged row swaps to the saved file IN PLACE
  // (renameTransform re-points the id), keeping its position among the
  // other scripts. The text that was posted IS the stored file, so the
  // row keeps it (TTTL-224): the editor rebaselines on it and a run
  // reads it — nothing fetches the file back.
  const adoptSavedScript = ({
    untitledId,
    serviceName,
    filename,
    content,
  }: SavedScript) => {
    const newId = `resource:${serviceName}:${filename}`;
    if (effectiveSelectedId === untitledId) markScriptSaved();
    renameTransform(untitledId, {
      id: newId,
      name: filename,
      provider: "udf",
    });
    // The saved file owns the content now — the draft renameTransform
    // carried over to the new id is already persisted.
    rememberSavedScript(newId, content);
    clearScriptDraft(newId);
    queryClient.invalidateQueries({
      queryKey: queryKeys.scripts(serviceName),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.serviceResources(serviceName),
    });
  };

  // Persists an untitled row's draft into the CHOSEN UDF service (the
  // Save-script modal's Existing tab).
  const saveNewInto = async ({ untitledId, serviceName, name }: ScriptHome) => {
    const draft = scriptDrafts[untitledId];
    if (!draft) return;
    const filename = withRhaiExtension(name);
    await scriptsApi.create(serviceName, filename, draft);
    adoptSavedScript({ untitledId, serviceName, filename, content: draft });
  };

  // Creates a brand-new UDF service CARRYING the draft (the modal's New
  // tab) — the engine writes the script file itself and namespaces the
  // script directory per service, so this is a single call.
  const saveIntoNewService = async ({
    untitledId,
    serviceName,
    name,
  }: ScriptHome) => {
    const draft = scriptDrafts[untitledId];
    if (!draft) return;
    const filename = withRhaiExtension(name);
    await servicesApi.create({
      provider: "udf",
      name: serviceName,
      script_path: `./scripts/${serviceName}`,
      engine: { kind: "rhai" },
      sources: [{ filename, content: draft }],
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.servicesRoot });
    adoptSavedScript({ untitledId, serviceName, filename, content: draft });
  };

  // Persists an untitled row's draft as a brand-new script in the
  // default (first) UDF service — the pre-modal path, kept for ⌘S-style
  // direct saves.
  const saveNew = async (untitledId: RowId, name: ScriptName) => {
    if (!scriptServiceName) return;
    await saveNewInto({ untitledId, serviceName: scriptServiceName, name });
  };

  const validateName = (trimmed: ScriptName) => {
    const filename = withRhaiExtension(trimmed);
    const taken = (existingScripts ?? []).some(
      (s) => s.filename === filename || s.filename.endsWith(`/${filename}`),
    );
    return taken
      ? {
          state: InputState.Warning,
          messages: ["A script with this name already exists"],
        }
      : null;
  };

  return {
    saveRow,
    saveRowAs,
    renameRow,
    saveNew,
    saveNewInto,
    saveIntoNewService,
    validateName,
  };
}
